package com.example.mystybag.login.logic

import com.example.mystybag.common.logic.DialogNavigatorForceUpdate
import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.login.LoginCommands.sendVerifyUser
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_CODE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USER_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_PUSH_TOKEN
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_APP_LANGUAGE
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.VibratorHelper
import com.example.mystybag.core.expected.openAppStorePage
import com.example.mystybag.core.expected.getLanguage
import com.example.mystybag.core.expected.LocaleHelper
import com.example.mystybag.sharedmodels.ModelsData
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.database.communication.db.DatabaseProvider
import com.example.mystybag.login.model.KeyButtonType
import com.example.mystybag.login.model.VerifyEmailAddressUIState
import com.example.mystybag.sharedmodels.base.BaseResponseModel
import com.example.mystybag.sharedmodels.defines.ServerStatusDefines.Companion.STATUS_MANDATORY_UPDATE
import com.example.mystybag.sharedmodels.project.requestModels.VerifyUserRequestModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

class VerifyEmailAddressViewModel(
    context: AppContext,
    val userId: Long,
    val email: String,
    val cityId: Long
): ChildViewModel<VerifyEmailAddressUIState>(context, VerifyEmailAddressUIState()) {

    override fun onCreate() {
        navControls.backButtonVisible = false
    }

    fun updateVerificationCode(code: String) {
        val numericCode = code.filter { it.isDigit() }.take(6)
        updateState { it.copy(layoutInput = numericCode) }
        if (numericCode.length == 6) {
            launchOnMain {
                sendVerifyCommand()
            }
        }
    }

    fun onKeyboardClick(key: KeyButtonType) {
        if (uiState.value.layoutInput.length == 6 && key != KeyButtonType.TYPE_BACK)
            return

        if (key == KeyButtonType.TYPE_FINGERPRINT || key == KeyButtonType.TYPE_FACE) {
            LogHelper.logEvent("Keyboard finger/face button click")
            return
        }

        if (key == KeyButtonType.TYPE_BACK) {
            if (uiState.value.layoutInput.isNotEmpty())
                VibratorHelper.vibrate()
            updateState { it.copy(layoutInput = uiState.value.layoutInput.dropLast(1)) }
        }
        else if (uiState.value.layoutInput.length < 6) {
            VibratorHelper.vibrate()
            updateState { it.copy(layoutInput = uiState.value.layoutInput.plus(key.value)) }
        }

        if (uiState.value.layoutInput.length == 6) {
            LogHelper.logEvent("Passcode entered")
            launchOnMain {
                sendVerifyCommand()
            }
        }
    }

    private fun sendVerifyCommand() {
        commandBlock {
            sendVerifyUser(
                request = VerifyUserRequestModel(
                    userId = userId,
                    cityId = cityId,
                    code = uiState.value.layoutInput
                )
            )
                .onResponse(
                    success = { response ->
                        try {
                            LogHelper.logEvent("VerifyEmailAddressViewModel: verifyUser success, setting preferences")
                            Communication.token = ""
                            Preferences.clear()
                            CoroutineScope(Dispatchers.Default).launch {
                                DatabaseProvider.clearDatabase()
                            }
                            
                            // Save preferences after verification (login will be done in HomePageViewModel)
                            Preferences.setString(PREFERENCES_EMAIL, email)
                            Preferences.setString(PREFERENCES_CODE, uiState.value.layoutInput)
                            Preferences.setLong(PREFERENCES_USER_ID, userId)
                            LogHelper.logEvent("VerifyEmailAddressViewModel: preferences set, launchOnMain goToMainScreen")

                            // Navigate to HomePage where login will be handled
                            launchOnMain {
                                LogHelper.logEvent("VerifyEmailAddressViewModel: inside launchOnMain, calling goToMainScreen")
                                setResult<LoginInterface> {
                                    goToMainScreen(false, onErrorCallback = {
                                        updateState { uiState -> uiState.copy() }
                                    })
                                }
                            }
                        } catch (e: Throwable) {
                            LogHelper.logEvent("VerifyEmailAddressViewModel: success callback error: ${e.message}")
                            com.example.mystybag.core.expected.logException(e as? Exception ?: Exception(e))
                        }
                    },
                    baseError = { response ->
                        LogHelper.logEvent("VerifyEmailAddressViewModel: verifyUser baseError status=${response.status}")
                        updateState { it.copy(layoutInput = "") }
                        handleVerifyEmailError(response)
                    }
                )
        }
    }

    private suspend fun handleVerifyEmailError(model: BaseResponseModel) {
        when (model.status) {
            STATUS_MANDATORY_UPDATE     -> showDialog(DialogNavigatorForceUpdate).onPositiveClick { openAppStorePage() }
            else                        -> showSnackbar(Response.getErrorMessage(model.status, model.apiEndPoint, model.statusMessage))
        }
    }
}